import { useEffect, useRef, type RefObject } from 'react';

/**
 * Hook to detect clicks outside a referenced element.
 * Extracted from 4 duplicated patterns (DUP-1):
 * AccountSwitcherDropdown, ProfileButton, ExportMenu.
 *
 * Uses a ref for the callback to prevent effect re-runs when
 * consumers pass inline arrows (ML-3 fix).
 *
 * @param ref - React ref to the container element
 * @param callback - Called when a click outside is detected
 * @param enabled - Only listen when true (default: true)
 */
export function useClickOutside(ref: RefObject<HTMLElement | null>, callback: () => void, enabled = true) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  useEffect(() => {
    if (!enabled) return;
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callbackRef.current();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, enabled]);
}